import { useEffect, useRef } from 'react'
import { useAction } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { events } from '@/models/analytics/analytics-events'
import { rootStore } from '@/models'
import type { AdvisorContext, TurnAdviceResponse } from '@ppwr/convex/players/types'
import { getAdvisorCoachingLevel, useActionAdvisorStore } from './useActionAdvisorStore'

type UseTurnAdviceArgs = {
  gameId?: Id<'games'>
  handNumber?: number
  userId?: string
  /** Game mode at the call site — stamped on the advice_shown analytics event. */
  gameMode?: string
  /** Composed client-side gating context from `useActionAdvisor` — present
   * only when the advisor is on and the game mode is supported. */
  advisorContext?: AdvisorContext
  /** True when it is the player's turn to act. */
  isPlayersTurn: boolean
  /** Number of actions taken in the current hand — identifies the spot. */
  handActionCount?: number
}

/**
 * Proactive turn-start advice ("Before I play" mode): fetches the advisor's
 * recommendation the moment the player's turn begins, so the bubble argues
 * the spot before they act.
 *
 * Contract:
 * - Fires at most once per spot (keyed by game/hand/action-count) — refetches
 *   on reconnect or re-render are suppressed; the server evaluation is
 *   deterministic per spot anyway.
 * - Stale results are discarded: if the spot moved on while the request was
 *   in flight (fast act, timeout, hand ended), the advice never lands and the
 *   player's action grade owns the bubble.
 * - Never blocks or gates the action controls — advice is a nicety; a failed
 *   request is silently dropped.
 *
 * Implementation note: the effect keys on PRIMITIVES (spotKey, booleans) —
 * `advisorContext` is a fresh object on every `useActionAdvisor` render, so
 * it must NOT be a dependency (it would re-run the effect on every game-doc
 * tick, whose cleanup cancels the in-flight request and silently discards
 * every advice response). Its value is read through a ref at call time.
 */
export function useTurnAdvice({
  gameId,
  handNumber,
  userId,
  gameMode,
  advisorContext,
  isPlayersTurn,
  handActionCount
}: UseTurnAdviceArgs) {
  const execute = useAction(api.players.actions.getTurnAdvice)
  const setAdvice = useActionAdvisorStore((state) => state.setAdvice)
  const clearAdvice = useActionAdvisorStore((state) => state.clearAdvice)
  const advice = useActionAdvisorStore((state) => state.advice)
  const advisorEnabled = useActionAdvisorStore((state) => state.enabled)
  const adviceBeforePlay = useActionAdvisorStore((state) => state.adviceBeforePlay)

  const spotKey =
    gameId && userId && typeof handNumber === 'number' && typeof handActionCount === 'number'
      ? `${gameId}:${userId}:${handNumber}:${handActionCount}`
      : undefined
  // Stable primitive for the effect deps: only the *presence* of a passing
  // advisor context gates the fetch (the server re-validates the mode).
  const advisorOn = advisorContext?.enabled === true

  // The context object itself is captured via ref (see the note above).
  const advisorContextRef = useRef(advisorContext)
  advisorContextRef.current = advisorContext

  // Tracks the spot an in-flight request is for, so a late response can be
  // discarded when the game moved on (next street, next hand, turn passed).
  // Read at completion time — the live value, not the render-time closure.
  const liveSpotKeyRef = useRef<string | undefined>(undefined)
  liveSpotKeyRef.current = spotKey
  const seenSpotKeysRef = useRef<Set<string>>(new Set())
  const prevPlayersTurnRef = useRef(isPlayersTurn)

  useEffect(() => {
    // Turn lost WITHOUT the player acting — a turn-timer auto-action (or the
    // hand ending server-side). Those paths make no client action call, so
    // nothing else clears the pending advice; left alone, the next graded
    // action would consume it as a false follow (thumbs-up for nothing).
    // The normal action flow clears the advice itself before executing, so by
    // the time this falling edge fires for a real action, `advice` is already
    // gone and this is a no-op.
    if (prevPlayersTurnRef.current && !isPlayersTurn && advice) {
      clearAdvice()
    }
    prevPlayersTurnRef.current = isPlayersTurn
  }, [isPlayersTurn, advice, clearAdvice])

  useEffect(() => {
    const shouldFetch =
      !!spotKey &&
      isPlayersTurn &&
      advisorEnabled &&
      adviceBeforePlay &&
      advisorOn &&
      !seenSpotKeysRef.current.has(spotKey)

    if (!shouldFetch) return

    seenSpotKeysRef.current.add(spotKey)
    const requestedSpotKey = spotKey
    const requestedAt = Date.now()
    let cancelled = false

    execute({ gameId: gameId!, userId: userId!, advisorContext: advisorContextRef.current })
      .then((result: TurnAdviceResponse) => {
        if (cancelled || !result) return
        // Staleness guard: only land the advice if the spot the request was
        // made for is still the live spot (player hasn't acted, street hasn't
        // advanced, hand hasn't ended).
        if (liveSpotKeyRef.current !== requestedSpotKey) return
        setAdvice(
          result.advice,
          { gameId: gameId!, userId: userId!, handNumber: handNumber! },
          // The store refuses advice whose request predates the last clear —
          // the player may have acted while this response was in flight (the
          // game subscription lags the action response, so the spot-key check
          // above alone cannot cover that window).
          requestedAt
        )
        // Analytics: the advice actually landed (post stale-guards). Reading
        // the live store keeps the coaching level accurate at event time.
        const storeState = useActionAdvisorStore.getState()
        rootStore?.analytics.setEvent(events.advisor.advice_shown, {
          coachingLevel: getAdvisorCoachingLevel(storeState),
          gameMode,
          street: result.advice.decision.street,
          concept: result.advice.decision.concept
        })
      })
      .catch(() => {
        // Advice is a nicety — a failed request is silently dropped.
      })

    return () => {
      cancelled = true
    }
  }, [
    spotKey,
    isPlayersTurn,
    advisorEnabled,
    adviceBeforePlay,
    advisorOn,
    gameId,
    userId,
    handNumber,
    execute,
    setAdvice
  ])
}
